import { useEffect, useRef, useState } from 'react';
import { useRequest } from 'domain/core/hooks/useRequest';
import { ApiMethodsEnum } from 'infrastructure/enums/ApiMethodsEnum';
import { debounce } from 'lodash';

const cache = new Map<string, any>();

export const useSearchAutocomplete = (
  endpoint: string,
  search: string,
  page: number,
  limit: number = 20
) => {
  const url = `${endpoint}?search=${encodeURIComponent(search.trim())}&page=${page}&limit=${limit}`;
  const cacheKey = url;

  const {
    data: fetchedData,
    send,
    isLoading,
    error,
    status,
  } = useRequest({
    url,
    method: ApiMethodsEnum.Get,
    sendOnStart: false,
  });

  const [cachedData, setCachedData] = useState(() => {
    return cache.get(cacheKey) || null;
  });

  const sendRef = useRef(send);
  sendRef.current = send;

  const prevSearchRef = useRef(search);

  const debouncedSend = useRef(
    debounce(() => {
      sendRef.current();
    }, 500)
  ).current;

  useEffect(() => {
    const cached = cache.get(cacheKey);
    if (cached) {
      setCachedData(cached);
      return;
    }

    const searchChanged = prevSearchRef.current !== search;
    prevSearchRef.current = search;

    if (searchChanged) {
      debouncedSend();
    } else {
      sendRef.current();
    }

    // eslint-disable-next-line consistent-return
    return () => {
      return debouncedSend.cancel();
    };
  }, [search, page]);

  useEffect(() => {
    if (fetchedData) {
      cache.set(cacheKey, fetchedData);
      setCachedData(fetchedData);
    }
  }, [fetchedData]);

  return { data: cachedData, isLoading, error, status };
};
